import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { hasAdminRoleInThisGroup } from './groupSlice';
import useGroupConvertor, { type Group } from '../domain/group';

import type { AppSelector } from '@/common/application/contextSlice';

export interface AdminGroup extends Group {adminOf?: string | number }
export interface GroupsContext {
    list: Group[],
    map: { [key: string]: Group },
    adminsLibrary: { [key: string]: AdminGroup },
    // documentId of the company the current `list` was loaded for — see the `context/setSuperGroup` case.
    superGroupRef?: string
}

const initialState: GroupsContext = { adminsLibrary: {}, list: [], map: {} };

const { parentGroupId } = useGroupConvertor;

// Create a slice for groups
const groupsSlice = createSlice({
    initialState,
    name: 'groups',
    // Referenced by action type string, NOT by importing `setSuperGroup`: contextSlice pulls in the
    // profile-selection controller, which reaches back into this slice — importing it here would close
    // that cycle and leave the action creator undefined while this module is being evaluated.
    extraReducers: (builder) => {
        // The list holds the groups of ONE subscriber (every `setGroups` caller loads them for the
        // selected company). Picking another subscriber invalidates it, so drop it immediately instead
        // of leaving the previous company's groups on screen until each screen refetches.
        builder.addCase('context/setSuperGroup', (state, action: PayloadAction<Group | undefined, 'context/setSuperGroup'>) => {
            const documentId = action.payload?.documentId;
            if (state.superGroupRef && state.superGroupRef !== documentId) {
                state.list = [];
                state.map = {};
            }
            state.superGroupRef = documentId;
        });
    },
    reducers: {
        
        // Reducer functions
        
        addAdminInLib: (state, action: PayloadAction<{key: string, group: AdminGroup}>) => {
            state.adminsLibrary[action.payload.key] = action.payload.group; 
        },
        clearGroups: () => initialState,
        removeGroup: (state, action: PayloadAction<string>) => {
            const documentId = action.payload;
            state.list = state.list.filter((group) => group.documentId !== documentId);
            Object.keys(state.map).forEach((key) => {
                if (state.map[key]?.documentId === documentId) delete state.map[key];
            });
        },
        setGroups: (state, action: PayloadAction<Group[]>) => {
            state.list = action.payload;
            state.map = {}; // Clear the map
            action.payload.forEach(item => {
                // If the group has a parent group and inherits its config, update the group config
                if (parentGroupId(item) && state.map[String(parentGroupId(item))]) {
                    const parentGroup = state.map[String(parentGroupId(item))];
                    item.groupConfig = parentGroup.groupConfig;
                }
                state.map[String(item.id)] = item;
            });
        },
    },
});

export const { setGroups, addAdminInLib, removeGroup } = groupsSlice.actions;
export default groupsSlice.reducer;

// Update the hasAdminRoleInAnyGroup action in the groupsSlice
export const hasAdminRoleInAnyGroup = (userRef?: string): AppSelector<boolean> => (state) => {
    return (() => {
        if (!userRef) {
            return false;
        }
    
        for (const group of state.groups.list) {
            
            const isAdm = hasAdminRoleInThisGroup(group.id as number, userRef)(state);
            if (isAdm) {
                return true;
            }
        }
    
        return false;
    })(); 
};